import { useState, useRef, useCallback } from "react";
import PatientService from "../Services/PatientService";

/**
 * usePatientSearch
 *
 * Encapsulates all patient-search and new-patient-registration logic.
 * Shared between AdmissionModal and BedAllocationModal so there is
 * zero duplication.
 *
 * Returns:
 *   mobile, setMobile          - controlled mobile input value
 *   searchState                - "idle" | "searching" | "found" | "not_found"
 *   patients                   - array of matched patients from API
 *   selectedPatient            - the patient object currently chosen
 *   showRegForm                - boolean — render new-patient registration form
 *   regForm, setRegField       - registration form state + field setter
 *   regErrors                  - validation errors for registration form
 *   regLoading                 - boolean — patient creation in progress
 *   searchError                - string — error from the search API call
 *   handleMobileChange         - onChange for the mobile <input>
 *   handleMobileKeyDown        - onKeyDown for the mobile <input> (Enter)
 *   triggerSearch              - call when Search icon is clicked
 *   selectPatient              - (patient) => void
 *   handleRegFieldChange       - onChange for any registration field
 *   submitRegistration         - registers + auto-selects newly created patient
 *   reset                      - wipe all state (call on modal close)
 */
const usePatientSearch = (loggedUser) => {
  // ── Mobile input ───────────────────────────────────────────────────────────
  const [mobile, setMobile] = useState("");
  const [name,setName]      = useState("");
  const [searchState, setSearchState] = useState("idle"); // idle|searching|found|not_found
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchError, setSearchError] = useState("");

  // ── Registration ───────────────────────────────────────────────────────────
  const [showRegForm, setShowRegForm] = useState(false);
  const [regForm, setRegForm] = useState({
    patientName: "",
    mobile: "",
    age: "",
    gender: "",
    address: "",
    email: "",
    dob: "",
    bloodGroup: "",
  });
  const [regErrors, setRegErrors] = useState({});
  const [regLoading, setRegLoading] = useState(false);

  // Guard against duplicate in-flight requests
  const searchInProgress = useRef(false);

  // ── Role-scoped base payload ────────────────────────────────────────────────
  const buildBasePayload = useCallback(
    (extra = {}) => {
      const base = {};
      const role = loggedUser?.user?.rolecode;

      if (role === "ACCT_ADM") {
        base.account_Id = loggedUser.user.account_Id;
      } else if (role === "CLINIC_ADM") {
        base.account_Id = loggedUser.user.account_Id;
        base.clinic_Id = loggedUser.user.clinic_Id;
      }

      return { ...base, ...extra };
    },
    [loggedUser]
  );

  // ── Validate registration form ─────────────────────────────────────────────
  const validateReg = (payload) => {
    const errors = {};

    if (!payload.patientName?.trim())
      errors.patientName = "Patient name is required";

    if (!payload.mobile?.trim())
      errors.mobile = "Mobile number is required";
    else if (!/^\d{10}$/.test(payload.mobile.trim()))
      errors.mobile = "Enter a valid 10-digit mobile number";

    if (payload.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email))
      errors.email = "Enter a valid email";

    if (!payload.gender) errors.gender = "Gender is required";

    if (!payload.age && !payload.dob)
      errors.age = "Age or date of birth is required";

    return errors;
  };

  // ── Core search ────────────────────────────────────────────────────────────
  const doSearch = useCallback(
    async (mobileVal) => {
      if (!/^\d{10}$/.test(mobileVal)) {
        setSearchError("Enter a valid 10-digit mobile number");
        return;
      }
      if (searchInProgress.current) return;

      searchInProgress.current = true;
      setSearchState("searching");
      setSearchError("");
      setPatients([]);
      setSelectedPatient(null);
      setShowRegForm(false);

      try {
        const payload = buildBasePayload({ mobile: mobileVal, status: 1 });
        const response = await PatientService.PatientList(payload);
        const list = response?.data || [];

        if (list.length > 0) {
          setPatients(list);
          setSearchState("found");
        } else {
          setSearchState("not_found");
          setShowRegForm(true);
          setRegForm((prev) => ({ ...prev, mobile: mobileVal }));
        }
      } catch {
        setSearchError("Failed to search patients. Please try again.");
        setSearchState("idle");
      } finally {
        searchInProgress.current = false;
      }
    },
    [buildBasePayload]
  );

  const doNameSearch = useCallback(
    async (nameVal) => {
      if (!nameVal) {
        setSearchError("Please fill in name field to search..");
        return;
      }
      if (searchInProgress.current) return;

      searchInProgress.current = true;
      setSearchState("searching");
      setSearchError("");
      setPatients([]);
      setSelectedPatient(null);
      setShowRegForm(false);

      try {
        const payload = buildBasePayload({ patientName: nameVal, status: 1 });
        const response = await PatientService.PatientList(payload);
        const list = response?.data || [];

        if (list.length > 0) {
          setPatients(list);
          setSearchState("found");
        } else {
          setSearchState("not_found");
          setShowRegForm(true);
          setRegForm((prev) => ({ ...prev, patientName: nameVal }));
        }
      } catch {
        setSearchError("Failed to search patients. Please try again.");
        setSearchState("idle");
      } finally {
        searchInProgress.current = false;
      }
    },
    [buildBasePayload]
  );

  // ── Handlers exposed to components ─────────────────────────────────────────
  const handleMobileChange = useCallback((e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
    setMobile(val);
    // Reset search state when user modifies the number
    setSearchState("idle");
    setPatients([]);
    setSelectedPatient(null);
    setShowRegForm(false);
    setSearchError("");
  }, []);

  // ── Handlers exposed to components ─────────────────────────────────────────
  const handleNameChange = useCallback((e) => {
    const val = e.target.value;
    setName(val);
    // Reset search state when user modifies the number
    setSearchState("idle");
    setPatients([]);
    setSelectedPatient(null);
    setShowRegForm(false);
    setSearchError("");
  }, []);

  const handleMobileKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") doSearch(mobile);
    },
    [mobile, doSearch]
  );

  const handleNameKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") doNameSearch(name);
    },
    [name, doNameSearch]
  );

  const triggerSearch = useCallback(() => {
    if(name) doNameSearch(name);
    if(mobile) doSearch(mobile);
  }, [name,mobile, doSearch,doNameSearch]);

  const selectPatient = useCallback((patient) => {
    setSelectedPatient(patient);
    // setPatients([]);
    setShowRegForm(false);
  }, []);

  const handleRegFieldChange = useCallback((e) => {
    const { name, value } = e.target;
    setRegForm((prev) => ({ ...prev, [name]: value }));
    setRegErrors((prev) => ({ ...prev, [name]: "" }));
  }, []);

  const submitRegistration = useCallback(async () => {
    const errors = validateReg(regForm);
    if (Object.keys(errors).length) {
      setRegErrors(errors);
      return;
    }

    setRegLoading(true);
    try {
      const payload = buildBasePayload({
        patientName: regForm.patientName.trim(),
        mobile: regForm.mobile.trim(),
        age: regForm.age ? Number(regForm.age) : undefined,
        gender: regForm.gender?.charAt(0) ?? "",
        dob: regForm.dob || undefined,
        bloodGroup: regForm.bloodGroup || undefined,
        email: regForm.email?.trim() || undefined,
        address: regForm.address?.trim() || undefined,
        status: 1,
      });

      await PatientService.AddPatient(payload);

      // Re-search so we can auto-select the newly registered patient
      searchInProgress.current = false;
      setShowRegForm(false);
      await doSearch(regForm.mobile);
    } catch {
      setRegErrors({ submit: "Registration failed. Please try again." });
    } finally {
      setRegLoading(false);
    }
  }, [regForm, buildBasePayload, doSearch]);

  // ── Reset everything on modal close ────────────────────────────────────────
  const reset = useCallback(() => {
    setMobile("");
    setName("");
    setSearchState("idle");
    setPatients([]);
    setSelectedPatient(null);
    setSearchError("");
    setShowRegForm(false);
    setRegForm({
      patientName: "",
      mobile: "",
      age: "",
      gender: "",
      address: "",
      email: "",
      dob: "",
      bloodGroup: "",
    });
    setRegErrors({});
    setRegLoading(false);
    searchInProgress.current = false;
  }, []);

  return {
    // mobile search
    name,
    mobile,
    searchState,
    patients,
    selectedPatient,
    searchError,
    // registration
    showRegForm,
    regForm,
    regErrors,
    regLoading,
    // handlers
    handleMobileChange,
    handleMobileKeyDown,
    triggerSearch,
    selectPatient,
    handleRegFieldChange,
    submitRegistration,
    reset,
    handleNameChange,
    handleNameKeyDown
  };
};

export default usePatientSearch;